import React, { useMemo, useState } from 'react';
import WizardShell from '../ui/wizard-shell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import type { TrialLessonDTO } from '../../lib/trials';
import { type MessageDraft, markTrialNotStarted } from '../../lib/trials';
import { toast } from 'sonner';
import CommunicationComposer, { type RecipientGroup } from '../communications/CommunicationComposer';
import CommunicationStandortHint from '../communications/CommunicationStandortHint';
import { WizardCard, WizardSection, WizardKVGrid, WizardKVItem, WizardYesNoBadge } from '../ui/wizard-layout';

export type NotStartedReasonKey =
  | 'no_suitable_appointment'
  | 'too_expensive'
  | 'student_teacher_mismatch'
  | 'instrument_not_fun'
  | 'concept_mismatch'
  | 'trial_other_school'
  | 'teacher_declined'
  | 'unknown';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trial: TrialLessonDTO;
  standortId?: number;
  onUpdated?: (trial: TrialLessonDTO) => void;
};
type StepState = 1 | 2 | 3;
type StepDelta = -1 | 1;

export default function MarkNotStartedWizardModal({ open, onOpenChange, trial, standortId, onUpdated }: Readonly<Props>) {
  const [step, setStep] = useState<StepState>(1);
  // Der bearbeitete Text aus dem Editor. Bisher ging stattdessen ein fester Satz raus (T-0067).
  const [entwurf, setEntwurf] = useState<MessageDraft | null>(null);
  // Die Lehrkraft erfährt, dass es nicht losgeht (T-0307).
  const [entwurfLehrkraft, setEntwurfLehrkraft] = useState<MessageDraft | null>(null);
  const [lehrkraftEmail, setLehrkraftEmail] = useState(true);
  const [lehrkraftSms, setLehrkraftSms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const moveStep = (delta: StepDelta) => {
    setStep((prev) => {
      const next = (prev + delta) as StepState;
      return Math.min(3, Math.max(1, next)) as StepState;
    });
  };

  const [reasonKey, setReasonKey] = useState<NotStartedReasonKey>('no_suitable_appointment');
  const [reasonText, setReasonText] = useState('');

  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);

  const reasonLabel = useMemo(() => {
    const base = (() => {
      if (reasonKey === 'no_suitable_appointment') return 'kein passender Termin';
      if (reasonKey === 'too_expensive') return 'Unterricht zu teuer';
      if (reasonKey === 'student_teacher_mismatch') return 'Schüler und Lehrer passen nicht zueinander';
      if (reasonKey === 'instrument_not_fun') return 'Instrument macht keinen Spaß';
      if (reasonKey === 'concept_mismatch') return 'Unterrichtskonzept passt nicht';
      if (reasonKey === 'trial_other_school') return 'Probetermin bei anderer Musikschule';
      if (reasonKey === 'teacher_declined') return 'Lehrer hat Angebot abgelehnt';
      return 'unbekannter Grund';
    })();
    const extra = reasonText.trim();
    return extra ? `${base}: ${extra}` : base;
  }, [reasonKey, reasonText]);

  const composerGroups: RecipientGroup[] = useMemo(() => {
    const sp = String((trial as any)?.subprofileId ?? (trial as any)?.subprofile_id ?? '').trim();
    if (!sp) return [];
    let wpUserId = 0;
    let subIndex: number | null = null;
    if (sp.includes('-')) {
      const [a, b] = sp.split('-', 2);
      wpUserId = Number.parseInt(a, 10) || 0;
      subIndex = b ? (Number.parseInt(b, 10) || null) : null;
    } else {
      wpUserId = Number.parseInt(sp, 10) || 0;
    }
    if (!wpUserId) return [];
    const vars = { reason: reasonLabel, studentName: trial.studentName || '', teacherName: trial.teacherName || '' };
    const gruppen: RecipientGroup[] = [{
      key: 'student',
      label: 'Teilnehmer',
      recipients: [{ id: `person:${wpUserId}:${subIndex ?? ''}`, type: 'person' as const, wpUserId, subIndex, name: trial.studentName || 'Teilnehmer' }],
      templateKeys: { email: 'trial.not_started.student', sms: 'trial.not_started.student' },
      vars,
      defaultSelectedChannels: { email: sendEmail, sms: sendSms },
    }];
    const lehrkraftId = Number(trial.teacherId || 0);
    if (lehrkraftId > 0) {
      gruppen.push({
        key: 'teacher',
        label: 'Lehrer',
        recipients: [{ id: `person:${lehrkraftId}:`, type: 'person' as const, wpUserId: lehrkraftId, subIndex: null, name: trial.teacherName || 'Lehrkraft' }],
        templateKeys: { email: 'trial.not_started.teacher', sms: 'trial.not_started.teacher' },
        vars,
        defaultSelectedChannels: { email: lehrkraftEmail, sms: lehrkraftSms },
      });
    }
    return gruppen;
  }, [trial, reasonLabel, sendEmail, sendSms, lehrkraftEmail, lehrkraftSms]);

  const canNext = useMemo(() => {
    if (step === 1) return reasonKey !== 'unknown' || reasonText.trim().length > 0;
    return true;
  }, [step, reasonKey, reasonText]);

  const lehrkraftAktiv = useMemo(
    () => ({
      email: composerGroups.some((g) => g.key === 'teacher') && lehrkraftEmail,
      sms: composerGroups.some((g) => g.key === 'teacher') && lehrkraftSms,
    }),
    [composerGroups, lehrkraftEmail, lehrkraftSms]
  );

  const submit = async () => {
    setIsSubmitting(true);
    try {
      const res = await markTrialNotStarted(trial.id, {
        reasonKey,
        reasonText: reasonText.trim() || undefined,
        standortId,
        channels: { email: sendEmail || lehrkraftAktiv.email, sms: sendSms || lehrkraftAktiv.sms },
        drafts: {
          ...(entwurf ? { student: entwurf } : {}),
          ...(entwurfLehrkraft && lehrkraftAktiv.email ? { teacher: entwurfLehrkraft } : {}),
        },
        reason: reasonLabel,
        notify: {
          student: { email: sendEmail, sms: sendSms },
          teacher: lehrkraftAktiv,
        },
      });
      const failed = [
        sendEmail && (res.email as any)?.ok === false ? 'E-Mail' : null,
        sendSms && (res.sms as any)?.ok === false ? 'SMS' : null,
        res.teacher?.emailError ? 'E-Mail an die Lehrkraft' : null,
        res.teacher?.smsError ? 'SMS an die Lehrkraft' : null,
      ].filter(Boolean);
      if (failed.length) toast.warning(`Status gesetzt; Versand fehlgeschlagen: ${failed.join(', ')}`);
      else toast.success('Status „nicht begonnen" wurde gesetzt');
      onUpdated?.(res.trial);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Fängt nicht an"
      description={`Schritt ${step} von 3`}
      currentStep={step}
      steps={[{ label: 'Grund' }, { label: 'Nachricht' }, { label: 'Übersicht' }]}
      onBack={step > 1 ? () => moveStep(-1) : undefined}
      canBack={step > 1 && !isSubmitting}
      onNext={step < 3 ? () => moveStep(1) : undefined}
      canNext={!isSubmitting && step < 3 ? canNext : false}
      onPrimary={step === 3 ? submit : undefined}
      primaryLabel={isSubmitting ? 'Speichere…' : 'Eintragen'}
      canPrimary={step === 3 && !isSubmitting}
      isLoading={isSubmitting}
    >
      <div className="space-y-4 py-4 motion-safe:animate-fade-up">
        {step === 1 && (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Warum soll der Unterricht nicht starten?</Label>
              <Select value={reasonKey} onValueChange={(v) => setReasonKey(v as NotStartedReasonKey)}>
                <SelectTrigger><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_suitable_appointment">kein passender Termin</SelectItem>
                  <SelectItem value="too_expensive">Unterricht zu teuer</SelectItem>
                  <SelectItem value="student_teacher_mismatch">Schüler und Lehrer passen nicht zueinander</SelectItem>
                  <SelectItem value="instrument_not_fun">Instrument macht keinen Spaß</SelectItem>
                  <SelectItem value="concept_mismatch">Unterrichtskonzept passt nicht</SelectItem>
                  <SelectItem value="trial_other_school">Probetermin bei anderer Musikschule</SelectItem>
                  <SelectItem value="teacher_declined">Lehrer hat Angebot abgelehnt</SelectItem>
                  <SelectItem value="unknown">unbekannter Grund</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Details (optional)</Label>
              <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="Freitext…" />
            </div>
          </div>
        )}

        {step === 2 && composerGroups.length > 0 && standortId > 0 && (
          <CommunicationComposer
            active={open && step === 2}
            standortId={standortId}
            groups={composerGroups}
            onDraftsChange={({ groups: g }) => {
              const s = g['student'];
              if (s) {
                setSendEmail(!!s.selectedChannels.email);
                setSendSms(!!s.selectedChannels.sms);
                setEntwurf({
                  subject: s.email.subjectTpl || '',
                  html: s.email.htmlTpl || '',
                  text: s.email.textTpl || '',
                  sms: s.sms.smsTpl || '',
                });
              }
              const t = g['teacher'];
              if (t) {
                setLehrkraftEmail(!!t.selectedChannels.email);
                setLehrkraftSms(!!t.selectedChannels.sms);
                setEntwurfLehrkraft({
                  subject: t.email.subjectTpl || '',
                  html: t.email.htmlTpl || '',
                  text: t.email.textTpl || '',
                  sms: t.sms.smsTpl || '',
                });
              }
            }}
          />
        )}
        {step === 2 && composerGroups.length > 0 && standortId <= 0 ? (
          <CommunicationStandortHint />
        ) : null}

        {step === 3 && (
          <WizardCard>
            <WizardSection title="Zusammenfassung">
              <WizardKVGrid>
                <WizardKVItem label="Grund">{reasonLabel}</WizardKVItem>
              </WizardKVGrid>
            </WizardSection>
            <WizardSection title="Benachrichtigungen">
              <WizardKVGrid>
                <WizardKVItem label="E-Mail"><WizardYesNoBadge yes={sendEmail} /></WizardKVItem>
                <WizardKVItem label="SMS"><WizardYesNoBadge yes={sendSms} /></WizardKVItem>
                {composerGroups.some((g) => g.key === 'teacher') ? (
                  <>
                    <WizardKVItem label="E-Mail an Lehrer"><WizardYesNoBadge yes={lehrkraftAktiv.email} /></WizardKVItem>
                    <WizardKVItem label="SMS an Lehrer"><WizardYesNoBadge yes={lehrkraftAktiv.sms} /></WizardKVItem>
                  </>
                ) : null}
              </WizardKVGrid>
            </WizardSection>
          </WizardCard>
        )}
      </div>
    </WizardShell>
  );
}
